/**
 * Named stacking layers. Import instead of raw z-index numbers.
 *
 * Layer guide (ascending):
 *   BASE       (0)   - Normal document flow
 *   FIELD      (1)   - Input surfaces above decorative prompt overlays
 *   STICKY     (5)   - Sticky labels, rulers
 *   CANVAS_SELECTION_OVERLAY (7) - Image-bound selection and mask preview overlays
 *   CLIPS      (10)  - Draggable clips, snap indicators
 *   SNAP       (15)  - Snap guides
 *   TOOLBAR    (25)  - Fixed app toolbars above canvas content
 *   TOOLBAR_ACTIVE (26) - Toolbar elements that sit above peer toolbar shells
 *   RIGHT_DOCK (35)  - Right dock shell above app chrome, below overlays
 *   CANVAS_CONTROLS (40) - Edit canvas zoom and history HUD above right dock chrome
 *   OVERLAY    (50)  - Canvas overlays, floating panels
 *   DROPDOWN   (100) - Dropdowns, popovers
 *   MODAL      (200) - Modal dialogs
 *   TOOLTIP    (300) - Tooltips
 *   TOAST      (400) - Toast notifications
 *   CANVAS_DROPDOWN (10101) - Portal menus that must sit above canvas items/HUDs
 *   COLLABORATION_CURSOR (10200) - Live collaborator cursors above canvas surfaces
 *   MEDIA_PICKER (10300) - Shared media picker above canvas HUDs and dropdowns
 *   LIGHTBOX   (2147483647) - Full-screen media preview above all app UI
 */
export const Z = {
  BASE: 0,
  FIELD: 1,
  STICKY: 5,
  CANVAS_SELECTION_OVERLAY: 7,
  CLIPS: 10,
  SNAP: 15,
  TOOLBAR: 25,
  TOOLBAR_ACTIVE: 26,
  RIGHT_DOCK: 35,
  CANVAS_CONTROLS: 40,
  OVERLAY: 50,
  DROPDOWN: 100,
  MODAL: 200,
  TOOLTIP: 300,
  TOAST: 400,
  CANVAS_DROPDOWN: 10101,
  COLLABORATION_CURSOR: 10200,
  MEDIA_PICKER: 10300,
  LIGHTBOX: 2147483647,
};

/**
 * Canvas-only stacking scale. Dynamic item data (`item.zIndex`) still belongs
 * to the canvas stack-role model; these slots are for fixed canvas chrome and
 * backing layers that used to carry local raw z-index literals.
 *
 * Intentional tie-breaks from the former 10100 HUD cluster:
 * scale badge < quick actions < generation notification < marquee actions <
 * primary single-item HUD < canvas dropdowns.
 */
export const CANVAS_Z = {
  // Screen-space WebGL visual plane sits behind the transform-layer stacking context.
  VISUAL_PLANE_BACKDROP: -10,
  WORKFLOW_SHELL_BODY_LAYER: -3,
  FRAME_BODY_LAYER: -2,
  THREAD_BACKGROUND_LAYER: -1,
  ITEM_STACK_BASE: 0,
  ITEM_STACK_MAX: Z.CANVAS_DROPDOWN - 256,
  DRAFT_PREVIEW: 1,
  LIQUIFY_SURFACE: 4,
  LIQUIFY_ABOVE_SURFACE: 5,
  THREAD_EDGE_LAYER: 6,
  IMAGE_BRUSH_OVERLAY: 7,
  IMAGE_BRUSH_CURSOR: 8,
  ITEM_SELECTION_OUTLINE: 9,
  ITEM_TRANSFORM_HANDLE: 10,
  THREAD_PORT_HIT_AREA: 11,
  GROUP_DROP_INDICATOR: Z.CANVAS_DROPDOWN - 100,
  MARQUEE_SELECTION_BOX: Z.CANVAS_DROPDOWN - 99,
  GROUP_SELECTION_FRAME: Z.CANVAS_DROPDOWN - 98,
  MINIMAP: 10050,
  WORKFLOW_SHELL_LABEL_OVERLAY: Z.CANVAS_DROPDOWN - 12,
  LABEL_OVERLAY: Z.CANVAS_DROPDOWN - 11,
  SELECTED_ITEM_CHROME: Z.CANVAS_DROPDOWN - 10,
  DRAGGING_ITEM: Z.CANVAS_DROPDOWN - 128,
  PLACEHOLDER_WRAPPER: Z.CANVAS_DROPDOWN - 127,
  HUD_SECONDARY_SCALE_BADGE: Z.CANVAS_DROPDOWN - 8,
  HUD_SECONDARY_QUICK_ACTIONS: Z.CANVAS_DROPDOWN - 7,
  HUD_SECONDARY_GENERATION_NOTIFICATION: Z.CANVAS_DROPDOWN - 6,
  HUD_MARQUEE_ACTION_DIALOG: Z.CANVAS_DROPDOWN - 5,
  HUD_PRIMARY: Z.CANVAS_DROPDOWN - 4,
  COLLABORATION_CURSOR: Z.COLLABORATION_CURSOR,
  INLINE_NAME_INPUT: Z.COLLABORATION_CURSOR + 2,
  AGENT_REGION_HALO: Z.COLLABORATION_CURSOR + 4,
  AGENT_SCAFFOLD: Z.COLLABORATION_CURSOR + 6,
  AGENT_SETTLE: Z.COLLABORATION_CURSOR + 8,
  AGENT_STATUS_CHIP: Z.COLLABORATION_CURSOR + 12,
  // Cursor chrome for click-to-place, so it must clear the canvas item stack and the HUDs
  // the pointer passes over. It carries no pointer events, so sitting this high costs
  // nothing to hit testing.
  PLACEMENT_CURSOR_TAG: Z.COLLABORATION_CURSOR + 1,
  DRAG_GHOST_PREVIEW: 999999,
  BRUSH_DRAFT: 2000000,
};
